import {Plugins, Component} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';

import {
  ORDER_BINDING_CONTEXT_ID,
  ORDER_CONFIRMATION_BINDING_CONTEXT_TYPE_ID,
  CHECKOUT_BINDING_CONTEXT_TYPE_ID,
  PAYPAL_CHECKOUT_BINDING_CONTEXT_TYPE_ID,
  PAYPAL_ORDER_BINDING_CONTEXT_ID,
  CONFIRMED_ORDER_BINDING_CONTEXT_ID,
  ORDER_ITEMS_BINDING_CONTEXT,
  ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
  ORDER_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT,
  PRODUCT_OPTION_BINDING_CONTEXT_TYPE_ID,
  SHIPPING_METHODS_BINDING_CONTEXT,
  SHIPPING_METHOD_BINDING_CONTEXT_TYPE_ID,
  EXTRA_ITEMS_BINDING_CONTEXT,
  EXTRA_ITEM_BINDING_CONTEXT_TYPE_ID,
  DOWNLOADS_BINDING_CONTEXT,
  DOWNLOAD_BINDING_CONTEXT_TYPE_ID,
} from './identifiers';

import {
  ORDER_QUERY_SPECIFICATION,
  ORDER_ITEMS_QUERY_SPECIFICATION,
} from './order-query-specification';

import type {
  BindingContextDef,
  BindingContextTypeDef,
  BindablePropertiesDef,
  TypeReferenceBuilder,
  BindingContextRelationshipDef,
  BindablePropertyDef,
} from '@packages/systems/dynamo/source-types';

export const orderType: BindingContextTypeDef = {
  entity: 'Entity',
  id: ORDER_CONFIRMATION_BINDING_CONTEXT_TYPE_ID,
  label: 'Order',
  pluralLabel: 'Orders',
  singularLabel: 'Order',
  iconName: 'product',
};

export const checkoutType: BindingContextTypeDef = {
  entity: 'Entity',
  id: CHECKOUT_BINDING_CONTEXT_TYPE_ID,
  label: 'Order',
  pluralLabel: 'Orders',
  singularLabel: 'Order',
  iconName: 'product',
};

export const paypalCheckoutType: BindingContextTypeDef = {
  entity: 'Entity',
  id: PAYPAL_CHECKOUT_BINDING_CONTEXT_TYPE_ID,
  label: 'Order',
  pluralLabel: 'Orders',
  singularLabel: 'Order',
  iconName: 'product',
};

export function orderBindingContexts<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  return [
    {
      variant: 'Entity',
      id: ORDER_BINDING_CONTEXT_ID,
      label: 'Order',
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceCheckoutFormContainer')
        ),
      },
      query: ORDER_QUERY_SPECIFICATION,
    },
    {
      variant: 'Entity',
      id: PAYPAL_ORDER_BINDING_CONTEXT_ID,
      label: 'Order',
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: PAYPAL_CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommercePaypalCheckoutFormContainer')
        ),
      },
      query: ORDER_QUERY_SPECIFICATION,
    },
    {
      variant: 'Entity',
      id: CONFIRMED_ORDER_BINDING_CONTEXT_ID,
      label: 'Order',
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: ORDER_CONFIRMATION_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceOrderConfirmationContainer')
        ),
      },
      query: ORDER_QUERY_SPECIFICATION,
    },
    {
      variant: 'List',
      id: ORDER_ITEMS_BINDING_CONTEXT,
      label: 'Products',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceCheckoutOrderItem')
        ),
      },
      query: ORDER_ITEMS_QUERY_SPECIFICATION,
    },
    {
      variant: 'List',
      id: ORDER_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT,
      label: 'Product Options',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: PRODUCT_OPTION_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceCheckoutOrderItemOptionListItem')
        ),
      },
    },
    {
      variant: 'List',
      id: SHIPPING_METHODS_BINDING_CONTEXT,
      label: 'Shipping Method',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: SHIPPING_METHOD_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceCheckoutShippingMethodItem')
        ),
      },
    },
    {
      variant: 'List',
      id: EXTRA_ITEMS_BINDING_CONTEXT,
      label: 'Extra Items',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: EXTRA_ITEM_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceCheckoutOrderSummaryExtraItemsListItem')
        ),
      },
    },
    {
      variant: 'List',
      id: DOWNLOADS_BINDING_CONTEXT,
      label: 'Downloads',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: DOWNLOAD_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Commerce'),
          Component.name('CommerceDownloadsItem')
        ),
      },
    },
  ];
}

export function orderRelationships<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextRelationshipDef<RootTRef, EntityTRef>[] {
  return [
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: ORDER_CONFIRMATION_BINDING_CONTEXT_TYPE_ID,
      }),
      relativeContexts: [
        ORDER_ITEMS_BINDING_CONTEXT,
        SHIPPING_METHODS_BINDING_CONTEXT,
        EXTRA_ITEMS_BINDING_CONTEXT,
        DOWNLOADS_BINDING_CONTEXT,
      ],
    },
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      relativeContexts: [
        ORDER_ITEMS_BINDING_CONTEXT,
        SHIPPING_METHODS_BINDING_CONTEXT,
        EXTRA_ITEMS_BINDING_CONTEXT,
      ],
    },
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: PAYPAL_CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      relativeContexts: [
        ORDER_ITEMS_BINDING_CONTEXT,
        SHIPPING_METHODS_BINDING_CONTEXT,
        EXTRA_ITEMS_BINDING_CONTEXT,
      ],
    },
  ];
}

export function orderBindableProperties<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindablePropertiesDef<EntityTRef, ListTRef>[] {
  return [
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: ORDER_CONFIRMATION_BINDING_CONTEXT_TYPE_ID,
      }),
      bindableProperties: [
        ...TOTALS_AND_EMAIL_BINDABLE_PROPERTIES,
        ...CARD_BINDABLE_PROPERTIES,
        ...ADDRESS_AND_MISC_BINDABLE_PROPERTIES,
      ],
    },
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      bindableProperties: [
        ...TOTALS_AND_EMAIL_BINDABLE_PROPERTIES,
        // Card fields are never populated on the Checkout page
        ...ADDRESS_AND_MISC_BINDABLE_PROPERTIES,
      ],
    },
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: PAYPAL_CHECKOUT_BINDING_CONTEXT_TYPE_ID,
      }),
      bindableProperties: [
        ...TOTALS_AND_EMAIL_BINDABLE_PROPERTIES,
        // TODO: Do card bindable properties work on PayPal checkout page?
        ...CARD_BINDABLE_PROPERTIES,
        ...ADDRESS_AND_MISC_BINDABLE_PROPERTIES,
      ],
    },
  ];
}

const TOTALS_AND_EMAIL_BINDABLE_PROPERTIES: BindablePropertyDef<
  never,
  never
>[] = [
  {
    id: 'total',
    label: 'Total',
    path: [
      {
        id: '',
        slug: 'total',
        type: 'CommercePrice',
      },
    ],
    queryPath: [],
    type: 'CommercePrice',
    meta: {
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'subtotal',
    label: 'Subtotal',
    path: [
      {
        id: '',
        slug: 'subtotal',
        type: 'CommercePrice',
      },
    ],
    queryPath: [],
    type: 'CommercePrice',
    meta: {
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'customerEmail',
    label: 'Customer Email',
    path: [
      {
        id: '',
        slug: 'customerEmail',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
];

const CARD_BINDABLE_PROPERTIES: BindablePropertyDef<never, never>[] = [
  {
    id: 'cardProvider',
    label: 'Card Provider',
    path: [
      {
        id: '',
        slug: 'cardProvider',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'cardLastFour',
    label: 'Card Last Four',
    path: [
      {
        id: '',
        slug: 'cardLastFour',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'cardExpiresMonth',
    label: 'Card Expires Month',
    path: [
      {
        id: '',
        slug: 'cardExpiresMonth',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'cardExpiresYear',
    label: 'Card Expires Year',
    path: [
      {
        id: '',
        slug: 'cardExpiresYear',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
];

const ADDRESS_AND_MISC_BINDABLE_PROPERTIES: BindablePropertyDef<
  never,
  never
>[] = [
  {
    id: 'shippingAddressAddressee',
    label: 'Shipping Address Addressee',
    path: [
      {
        id: '',
        slug: 'shippingAddressAddressee',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressLine1',
    label: 'Shipping Address Line 1',
    path: [
      {
        id: '',
        slug: 'shippingAddressLine1',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressLine2',
    label: 'Shipping Address Line 2',
    path: [
      {
        id: '',
        slug: 'shippingAddressLine2',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressCity',
    label: 'Shipping Address City',
    path: [
      {
        id: '',
        slug: 'shippingAddressCity',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressState',
    label: 'Shipping Address State',
    path: [
      {
        id: '',
        slug: 'shippingAddressState',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressPostalCode',
    label: 'Shipping Address Postal Code',
    path: [
      {
        id: '',
        slug: 'shippingAddressPostalCode',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingAddressCountry',
    label: 'Shipping Address Country',
    path: [
      {
        id: '',
        slug: 'shippingAddressCountry',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressAddressee',
    label: 'Billing Address Addressee',
    path: [
      {
        id: '',
        slug: 'billingAddressAddressee',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressLine1',
    label: 'Billing Address Line 1',
    path: [
      {
        id: '',
        slug: 'billingAddressLine1',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressLine2',
    label: 'Billing Address Line 2',
    path: [
      {
        id: '',
        slug: 'billingAddressLine2',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressCity',
    label: 'Billing Address City',
    path: [
      {
        id: '',
        slug: 'billingAddressCity',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressState',
    label: 'Billing Address State',
    path: [
      {
        id: '',
        slug: 'billingAddressState',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressPostalCode',
    label: 'Billing Address Postal Code',
    path: [
      {
        id: '',
        slug: 'billingAddressPostalCode',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'billingAddressCountry',
    label: 'Billing Address Country',
    path: [
      {
        id: '',
        slug: 'billingAddressCountry',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingMethodName',
    label: 'Shipping Method Name',
    path: [
      {
        id: '',
        slug: 'shippingMethodName',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'shippingMethodDescription',
    label: 'Shipping Method Description',
    path: [
      {
        id: '',
        slug: 'shippingMethodDescription',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'userItemsCount',
    label: 'Count',
    path: [
      {
        id: '',
        slug: 'userItemsCount',
        type: 'Number',
      },
    ],
    queryPath: [],
    type: 'Number',
    meta: {
      format: 'any',
      precision: null,
      allowNegative: false,
      minValue: null,
      maxValue: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'requiresShipping',
    label: 'Requires Shipping',
    path: [
      {
        id: '',
        slug: 'requiresShipping',
        type: 'Bool',
      },
    ],
    queryPath: [],
    type: 'Bool',
    meta: {
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'additionalTextInput',
    label: 'Additional TextInput',
    path: [
      {
        id: '',
        slug: 'additionalTextInput',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'additionalTextArea',
    label: 'Additional Textarea',
    path: [
      {
        id: '',
        slug: 'additionalTextArea',
        type: 'PlainText',
      },
    ],
    queryPath: [],
    type: 'PlainText',
    meta: {
      singleLine: true,
      minLength: null,
      maxLength: null,
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'additionalCheckbox',
    label: 'Additional Checkbox',
    path: [
      {
        id: '',
        slug: 'additionalCheckbox',
        type: 'Bool',
      },
    ],
    queryPath: [],
    type: 'Bool',
    meta: {
      required: true,
    },
    rendering: {strategy: 'client'},
  },
  {
    id: 'hasDownloads',
    label: 'Has Downloads',
    path: [
      {
        id: '',
        slug: 'hasDownloads',
        type: 'Bool',
      },
    ],
    queryPath: [],
    type: 'Bool',
    meta: {
      required: true,
    },
    rendering: {strategy: 'client'},
  },
];
